import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonFloat } from './php-decimal-values';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot materialize inventory weight on a scalar');
    return Object.fromEntries(Object.entries(value));
}
export interface InventoryBaseEconomyReaders {
    itemField: (row: ItemFieldRow, key: string) => unknown;
    layerValue: (row: ItemFieldRow, name: string) => unknown;
    /** Exact resolved derivation, including known null. Missing results must throw.
     * The dependency owns enrollment, mesh selection/profile and material density.
     */
    derivedWeight: (row: ItemFieldRow, sizeScale: number) => Readonly<{ unit_lb: unknown }> | null;
}
/** Contiguous cost/weight base stage after permissions and before enchantment.
 * Wrapped root mirrors are temporary PHP intermediates; canonical weight can be
 * zero even though the temporary weight mirror is floored at one hundredth.
 */
export function compileInventoryBaseEconomy(target: ItemFieldRow, sizeScale: number, readers: InventoryBaseEconomyReaders): ItemFieldRow {
    const out = writable(clone(target));
    const costOverride = readers.itemField(out, 'inventory_cost_gp');
    const cost = readers.layerValue(out, 'cost_gp');
    out.cost_gp = [costOverride !== null ? phpJsonFloat(costOverride) : Math.max(phpJsonFloat(cost ?? 0.01), 0.01)];
    const weightOverride = readInventoryField(out, 'weight_override');
    const materializeWeight = (value: unknown): void => {
        const layers = writable(out.item_layer_types); layers.weight = value; out.item_layer_types = layers;
    };
    if (weightOverride !== null) {
        const weight = phpJsonFloat(weightOverride);
        // PHP max keeps its first argument on equality, including a signed zero.
        materializeWeight(weight >= 0 ? weight : 0);
    }
    else {
        const derived = readers.derivedWeight(out, sizeScale);
        if (derived !== null) materializeWeight(derived.unit_lb);
    }
    out.weight = [Math.max(phpJsonFloat(readers.layerValue(out, 'weight') ?? 0.01), 0.01)];
    return clone(out) as ItemFieldRow;
}
export interface InventoryBaseEconomyDependencies extends Required<ItemFieldDependencies> {
    catalogRows: ProviderCatalogRows;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    derivedWeight: InventoryBaseEconomyReaders['derivedWeight'];
    trace?: (event: readonly unknown[]) => void;
}
export function createInventoryBaseEconomyCompiler(deps: InventoryBaseEconomyDependencies): (row: ItemFieldRow, sizeScale: number) => ItemFieldRow {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId, resolveCarrierRef: reference });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const fields: ItemFieldDependencies = { resolveCarrierRef: reference,
        resolveItemName: id => { emit(['name', id]); return deps.resolveItemName(id); },
        resolveInventoryIdentity: id => { emit(['inventory-identity', id]); return deps.resolveInventoryIdentity(id); } };
    return (row, sizeScale) => compileInventoryBaseEconomy(row, sizeScale, {
        itemField: (target, key) => readItemField(target, key, fields), layerValue: scalar.read,
        derivedWeight: (target, scale) => { emit(['derived-weight', clone(target), scale]); return deps.derivedWeight(target, scale); },
    });
}
